import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { ignore } from 'utility/ignore';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { timestampFromOclock } from 'utility/timestampFromOclock';

type HeartbeatPacket = {
    type: 'Heartbeat';
    body: {
        'project_id': Project.Id;
        'vehicle_id': Vehicle.Id;
        'payload': string;
    };
}

type HeartbeatAckPacket = {
    type: 'HeartbeatAck';
    body: {
        'project_id': Project.Id;
        'vehicle_id': Vehicle.Id;
        'payload': string;
    };
}

function isHeartbeatAck(json: HeartbeatAckPacket | unknown[]): json is HeartbeatAckPacket {
    return typeof json === 'object' && json !== null && 'type' in json && json.type == 'HeartbeatAck';
}

export class VehicleStandByConnection {
    constructor(private ws: StableWebSocket) {
        ws.addEventListener('message', msg => {
            const json = JSON.parse(msg.data);
            if (!isHeartbeatAck(json)) return;
            const [timestamp, accept] = json.body.payload.split(',', 2);
            this.onAck(json.body.vehicle_id, json.body.project_id, parseFloat(timestamp), accept == '1');
        });
    }

    async post(vehicleId: Vehicle.Id, projectId: Project.Id) {
        const packet: HeartbeatPacket = {
            type: 'Heartbeat',
            body: {
                'vehicle_id': vehicleId,
                'project_id': projectId,
                payload: [timestampFromOclock()].join(','),
            },
        };
        await this.ws.send(JSON.stringify(packet));
    }

    private readonly listenerManager = createKeyedListenerManager<Vehicle.Id, (timestamp: number, accept: boolean) => void>();

    onAck(vehicleId: Vehicle.Id, projectId: Project.Id, timestamp: number, accept: boolean) {
        ignore(projectId);
        this.listenerManager.forEach(vehicleId, listener => listener(timestamp, accept));
    }

    addAckListener(vehicleId: Vehicle.Id, listener: (timestamp: number, accept: boolean) => void) {
        this.listenerManager.addListener(vehicleId, listener);
    }

    removeAckListener(vehicleId: Vehicle.Id, listener: (timestamp: number, accept: boolean) => void) {
        this.listenerManager.removeListener(vehicleId, listener);
    }
}
